import React, { useEffect, useMemo, useState } from 'react';
import { Cross, Plus } from '@saleshandy/icons';
import { useDispatch, useSelector } from 'react-redux';
import { SkeletonLoading } from '@saleshandy/design-system';

import {
  OverlayPopover,
  Placement,
} from '../../../shared/design-system/components/overlay';
import hasPermission from '../../../shared/utils/access-control/has-permission';
import { Permissions } from '../../../shared/utils/access-control/enums/permissions';
import { accessibleOnClick } from '../../../shared/utils/accessible-on-click';
import { InframailData } from '../types';

import { InframailIpStatus } from '../enums';
import { getInframailIpsDomainsRequest } from '../extra-actions';
import { RootState } from '../../../store/root-reducer';

type InframailIpsPopoverProps = {
  cell: number;
  row: InframailData;
  type: 'domains' | 'mailboxes';

  onAddMoreDomainsClicked: (row: InframailData) => void;
  onAddMoreEmailAccountsClicked: (row: InframailData) => void;
};

const InframailIpsPopover: React.FC<InframailIpsPopoverProps> = ({
  cell,
  row,
  type,
  onAddMoreDomainsClicked,
  onAddMoreEmailAccountsClicked,
}) => {
  const dispatch = useDispatch();

  const inframailIpsDomains = useSelector(
    (state: RootState) => state.inframailIps.inframailIpsDomains,
  );
  const inframailIpsDomainsLoading = useSelector(
    (state: RootState) => state.inframailIps.inframailIpsDomainsLoading,
  );

  const [showPopover, setShowPopover] = useState(false);

  useEffect(() => {
    if (
      showPopover &&
      (!inframailIpsDomainsLoading || !inframailIpsDomainsLoading[row.id]) &&
      (!inframailIpsDomains || !inframailIpsDomains[row.id])
    ) {
      dispatch(getInframailIpsDomainsRequest(row.id));
    }
  }, [showPopover]);

  const renderItems = (item: { id: string; value: string }) => (
    <div className="domains-mailbox-item" key={item.id}>
      <p className="domains-mailbox-item__email">{item.value}</p>
    </div>
  );

  const renderAddButton = () => {
    if (hasPermission(Permissions.DOMAIN_READ) && row.totalMailboxes < 400) {
      const btn = (
        <div
          className="add-more-btn-infra"
          {...accessibleOnClick(() => {
            if (type === 'domains') {
              onAddMoreDomainsClicked(row);
              return;
            }
            onAddMoreEmailAccountsClicked(row);
          })}
        >
          <Plus />
          <span>Add More</span>
        </div>
      );

      if (type === 'domains' && row.status === InframailIpStatus.Active) {
        return btn;
      }

      if (
        type === 'mailboxes' &&
        (row.status === InframailIpStatus.Active ||
          row.status === InframailIpStatus.InProgress)
      ) {
        return btn;
      }
    }

    return <></>;
  };

  const data = useMemo(() => {
    const domains: { id: string; value: string }[] = [];
    const mailboxes: { id: string; value: string }[] = [];

    if (inframailIpsDomains && inframailIpsDomains[row.id]) {
      inframailIpsDomains?.[row.id]?.forEach((domain, index) => {
        domains.push({
          id: `domain-${index}`,
          value: domain.name,
        });

        domain.mailboxes.forEach((mailbox) => {
          mailboxes.push({
            id: mailbox.id,
            value: mailbox.email,
          });
        });
      });
    }

    return { domains, mailboxes };
  }, [inframailIpsDomains]);

  const isLoading = useMemo(
    () => !inframailIpsDomainsLoading || inframailIpsDomainsLoading[row.id],
    [inframailIpsDomainsLoading],
  );

  return (
    <div className="domains-mailboxes">
      {!cell || cell === 0 ? (
        <span className="table-cell-text">{cell ?? '-'}</span>
      ) : (
        <OverlayPopover
          show={showPopover}
          onToggle={setShowPopover}
          className="domains-mailboxes-popover"
          content={
            <>
              <div className="domains-mailboxes">
                {isLoading ? (
                  <>
                    <SkeletonLoading width={200} height={20} />
                    <SkeletonLoading width={200} height={20} />
                    <SkeletonLoading width={200} height={20} />
                  </>
                ) : (
                  data[type].map((item) => renderItems(item))
                )}
              </div>
              <Cross
                className="cursor-pointer"
                color="#1F2937"
                onClick={() => setShowPopover(false)}
              />
            </>
          }
          placement={Placement.AutoStart}
          rootClose
        >
          <span
            className={`table-cell-text domains-mailboxes-count ${
              showPopover ? 'active' : ''
            }`}
          >
            {cell ?? '-'}
          </span>
        </OverlayPopover>
      )}
      {renderAddButton()}
    </div>
  );
};

export default InframailIpsPopover;
